import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useFinance } from "./FinanceContext";
import { dl, now, sumTo } from "./data/model";
import { MONTHS, SEED } from "./data/seedData";
import { AnnualBar } from "./components/AnnualBar";
import { MonthInline } from "./components/MonthPicker";
import { StatusBadge } from "./components/StatusChip";
import { toneColor } from "./components/tone";

function csvCell(s: unknown): string {
  return '"' + String(s == null ? "" : s).replace(/"/g, '""') + '"';
}
const R = Math.round;

const REPORT_NAMES = [
  "Budget vs Actual Summary",
  "Version Comparison",
  "Validation Errors",
  "Approval History",
  "Change Log",
  "Audit Trail",
  "Revenue Changes",
  "Budget Changes",
  "Rollback History",
  "Frozen Period Report",
  "Department Report",
  "Cost Centre Report",
] as const;
type ReportName = (typeof REPORT_NAMES)[number];

export function ReportsPage() {
  const { model, fmt, scf, selMonth, versions, audit, locks, staged, logAudit } = useFinance();

  const catRows = SEED.categories.map((cat) => {
    const its = model.items.filter((i) => i.catLetter === cat.letter);
    let annual = 0;
    let ytdB = 0;
    let ytdA = 0;
    its.forEach((it) => {
      annual += it.planned.reduce((s, v) => s + v, 0) * scf;
      ytdB += sumTo(it.planned, selMonth) * scf;
      ytdA += sumTo(it.actual, selMonth);
    });
    return { cat, annual, ytdB, ytdA, v: ytdB - ytdA, pct: ytdB ? (ytdA / ytdB) * 100 : 0 };
  });
  const tot = catRows.reduce(
    (o, r) => ({ annual: o.annual + r.annual, ytdB: o.ytdB + r.ytdB, ytdA: o.ytdA + r.ytdA }),
    { annual: 0, ytdB: 0, ytdA: 0 },
  );

  function grouped(key: "dept" | "cc") {
    const g: Record<string, { annual: number; ytdB: number; ytdA: number }> = {};
    model.items.forEach((it) => {
      const k = it[key];
      g[k] ??= { annual: 0, ytdB: 0, ytdA: 0 };
      const entry = g[k];
      if (!entry) return;
      entry.annual += it.planned.reduce((s, v) => s + v, 0) * scf;
      entry.ytdB += sumTo(it.planned, selMonth) * scf;
      entry.ytdA += sumTo(it.actual, selMonth);
    });
    return g;
  }

  function build(name: ReportName): { csv: string; filename: string; n: number } {
    let c = "";
    let fn = "report";
    let n = 0;
    if (name === "Budget vs Actual Summary") {
      c = "Code,Category,Type,AnnualBudget,YTDBudget,YTDActual,Variance,Util%\n";
      catRows.forEach((r) => {
        c += `${r.cat.code},${csvCell(r.cat.name)},${r.cat.type},${R(r.annual)},${R(r.ytdB)},${R(r.ytdA)},${R(r.v)},${r.pct.toFixed(1)}%\n`;
        n++;
      });
      c += `,GRAND TOTAL,,${R(tot.annual)},${R(tot.ytdB)},${R(tot.ytdA)},${R(tot.ytdB - tot.ytdA)},${tot.ytdB ? ((tot.ytdA / tot.ytdB) * 100).toFixed(1) : "0.0"}%\n`;
      fn = "budget-vs-actual";
    } else if (name === "Version Comparison") {
      c = "Version,Kind,Status,Active,Parent,By,Approved,Frozen,Published,Reason\n";
      versions.forEach((v) => {
        c += `${v.ver},${v.kind || "Actuals"},${v.status},${v.active ? "Yes" : "No"},${v.parentVersion || "—"},${csvCell(v.uploadedBy)},${csvCell(v.approvedDate || "—")},${csvCell(v.freezeDate || "—")},${csvCell(v.publishedDate || "—")},${csvCell(v.reason || v.label || "")}\n`;
        n++;
      });
      fn = "version-history";
    } else if (name === "Validation Errors") {
      c = "Line,Code,Rule,Message,Severity\n";
      const e = staged?.validation.errors ?? [];
      if (e.length)
        e.forEach((x) => {
          c += `${csvCell(x.line)},${csvCell(x.code)},${csvCell(x.rule)},${csvCell(x.message)},${x.severity}\n`;
          n++;
        });
      else c += ",,,No pending validation errors (no Tally file currently staged),\n";
      fn = "validation-errors";
    } else if (name === "Approval History") {
      c = "Date,User,Role,Action,Version,Detail\n";
      audit
        .filter((a) => /appro|reject|submit|publish|frozen|revision|withdraw/i.test(a.action))
        .forEach((a) => {
          c += `${csvCell(a.date)},${csvCell(a.by)},${csvCell(a.role)},${csvCell(a.action)},${csvCell(a.ver)},${csvCell(a.detail)}\n`;
          n++;
        });
      fn = "approval-history";
    } else if (name === "Change Log") {
      c = "Date,User,Action,Detail\n";
      audit
        .filter((a) => a.action !== "Login" && a.action !== "Role switched")
        .forEach((a) => {
          c += `${csvCell(a.date)},${csvCell(a.by)},${csvCell(a.action)},${csvCell(a.detail)}\n`;
          n++;
        });
      fn = "change-log";
    } else if (name === "Audit Trail") {
      c = "Action,User,Role,Version,Timestamp,Reason,Detail\n";
      audit.forEach((a) => {
        c += `${csvCell(a.action)},${csvCell(a.by)},${csvCell(a.role)},${csvCell(a.ver)},${csvCell(a.date)},${csvCell(a.reason)},${csvCell(a.detail)}\n`;
        n++;
      });
      fn = "audit-trail";
    } else if (name === "Revenue Changes") {
      c = `Stream,${MONTHS.map((m) => m + " Plan").join(",")},${MONTHS.map((m) => m + " Actual").join(",")},FY Plan,FY Actual\n`;
      model.revenue.forEach((r) => {
        c += `${csvCell(r.name)},${r.planned.map(R).join(",")},${r.actual.map(R).join(",")},${R(r.planned.reduce((s, v) => s + v, 0))},${R(r.actual.reduce((s, v) => s + v, 0))}\n`;
        n++;
      });
      fn = "revenue";
    } else if (name === "Budget Changes") {
      c = "Code,Cost Head,Type,ApprovedBudget,Allocated\n";
      model.items.forEach((it) => {
        c += `${it.led},${csvCell(it.name)},${it.type},${R(it.approved || 0)},${R(it.planned.reduce((s, v) => s + v, 0))}\n`;
        n++;
      });
      fn = "budget-changes";
    } else if (name === "Rollback History") {
      c = "Version,Label,By,Date,Status\n";
      const rb = versions.filter((v) => v.status === "Rolled Back" || /^rb/.test(v.id));
      if (rb.length)
        rb.forEach((v) => {
          c += `${v.ver},${csvCell(v.label)},${csvCell(v.uploadedBy)},${csvCell(v.publishedDate || v.uploadDate)},${v.status}\n`;
          n++;
        });
      else c += ",No rollbacks performed to date,,,\n";
      fn = "rollback-history";
    } else if (name === "Frozen Period Report") {
      c = "Month,Status,Freeze Level,Locked By,Date,Budget,Actual\n";
      MONTHS.forEach((m, i) => {
        let b = 0;
        let a = 0;
        model.items.forEach((it) => {
          b += (it.planned[i] ?? 0) * scf;
          a += it.actual[i] ?? 0;
        });
        const lock = locks[i];
        c += `${m},${lock ? "Locked" : "Editable"},${lock ? lock.level : "—"},${lock ? csvCell(lock.by) : "—"},${lock ? lock.date : "—"},${R(b)},${R(a)}\n`;
        n++;
      });
      fn = "frozen-periods";
    } else {
      const key = name === "Department Report" ? "dept" : "cc";
      const g = grouped(key);
      c =
        (key === "dept" ? "Department" : "Cost Centre") +
        ",AnnualBudget,YTDBudget,YTDActual,Variance,Util%\n";
      Object.keys(g).forEach((k) => {
        const x = g[k];
        if (!x) return;
        c += `${csvCell(k)},${R(x.annual)},${R(x.ytdB)},${R(x.ytdA)},${R(x.ytdB - x.ytdA)},${x.ytdB ? ((x.ytdA / x.ytdB) * 100).toFixed(1) : "0.0"}%\n`;
        n++;
      });
      fn = key;
    }
    return {
      csv: c,
      filename: `haystack-${fn}-${(MONTHS[selMonth] ?? "").replace(" ", "-")}.csv`,
      n,
    };
  }

  function download(name: ReportName) {
    const r = build(name);
    dl(r.csv, r.filename, "text/csv");
    logAudit("Report exported", `${name} exported to CSV (${r.n} rows)`);
  }

  function parseCsv(txt: string): string[][] {
    return txt
      .replace(/\n$/, "")
      .split("\n")
      .map((line) => {
        const out: string[] = [];
        let cur = "";
        let q = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (q) {
            if (ch === '"') {
              if (line[i + 1] === '"') {
                cur += '"';
                i++;
              } else q = false;
            } else cur += ch;
          } else if (ch === '"') q = true;
          else if (ch === ",") {
            out.push(cur);
            cur = "";
          } else cur += ch;
        }
        out.push(cur);
        return out;
      });
  }

  function printReport(name: ReportName) {
    const r = build(name);
    const w = window.open("", "_blank", "width=980,height=760");
    if (!w) {
      window.alert("Please allow pop-ups to open the printable / PDF view.");
      return;
    }
    const rows = parseCsv(r.csv);
    const head = rows[0] ?? [];
    const body = rows.slice(1);
    const isNum = (s: string | undefined) =>
      s !== undefined && s !== "" && s !== "—" && !Number.isNaN(Number(s.replace(/[, ]/g, "")));
    const th = head
      .map((h) => `<th class="${isNum(body[0]?.[head.indexOf(h)]) ? "num" : ""}">${h}</th>`)
      .join("");
    const tb = body
      .map(
        (row) =>
          "<tr>" +
          row.map((cell) => `<td class="${isNum(cell) ? "num" : ""}">${cell ?? ""}</td>`).join("") +
          "</tr>",
      )
      .join("");
    w.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${name} — ${SEED.fiscalYear}</title>` +
        "<style>body{font-family:Segoe UI,Arial,sans-serif;color:#1e2233;padding:26px;margin:0}.brand{color:#5b53ff;font-weight:800}h1{font-size:18px;margin:0 0 2px}h2{font-size:12.5px;color:#666;font-weight:400;margin:0 0 16px}table{border-collapse:collapse;width:100%;font-size:11.5px}th,td{border:1px solid #d3d8e6;padding:6px 9px;text-align:left;vertical-align:top}th{background:#eef1f8;text-transform:uppercase;font-size:10.5px;letter-spacing:.03em}td.num,th.num{text-align:right;white-space:nowrap}tr:nth-child(even) td{background:#fafbff}.meta{color:#777;font-size:10.5px;margin-top:18px;border-top:1px solid #e3e7f0;padding-top:8px}.amt{color:#777;font-size:11px;margin:0 0 12px}@media print{body{padding:0}.noprint{display:none}}</style></head><body>" +
        `<h1><span class="brand">Haystack</span> — ${name}</h1>` +
        `<h2>${SEED.company} · ${SEED.fiscalYear} · Reporting month: ${MONTHS[selMonth]}</h2>` +
        `<p class="amt">Amounts in USD.</p>` +
        `<table><thead><tr>${th}</tr></thead><tbody>${tb}</tbody></table>` +
        `<div class="meta">Generated ${now()} · ${body.length} rows · Confidential — for internal use only.</div>` +
        `<div class="noprint" style="margin-top:18px"><button onclick="window.print()" style="padding:9px 16px;border:none;border-radius:8px;background:#5b53ff;color:#fff;font-weight:700;cursor:pointer">Print / Save as PDF</button></div>` +
        "</body></html>",
    );
    w.document.close();
    w.focus();
    setTimeout(() => {
      try {
        w.print();
      } catch {
        /* pop-up blocked or print unsupported — user can use the on-page button */
      }
    }, 400);
    logAudit("Report printed", `${name} opened as printable / PDF`);
  }

  const counts: Partial<Record<ReportName, number | string>> = {
    "Version Comparison": versions.length,
    "Audit Trail": audit.length,
    "Approval History": audit.filter((a) =>
      /appro|reject|submit|publish|frozen|revision|withdraw/i.test(a.action),
    ).length,
    "Change Log": audit.filter((a) => a.action !== "Login" && a.action !== "Role switched").length,
    "Validation Errors": staged?.validation.errors.length ?? 0,
    "Revenue Changes": model.revenue.length,
    "Budget Changes": model.items.length,
    "Rollback History": versions.filter((v) => v.status === "Rolled Back" || /^rb/.test(v.id))
      .length,
    "Frozen Period Report": `${locks.filter(Boolean).length}/12`,
    "Department Report": Object.keys(grouped("dept")).length,
    "Cost Centre Report": Object.keys(grouped("cc")).length,
    "Budget vs Actual Summary": catRows.length,
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Reports &amp; Exports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Click any report to download a CSV · print for PDF · YTD through {MONTHS[selMonth]}
      </Typography>
      <AnnualBar />
      <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => download("Budget vs Actual Summary")}>
          Export Budget vs Actual (CSV)
        </Button>
        <Button variant="outlined" onClick={() => printReport("Budget vs Actual Summary")}>
          Print / PDF
        </Button>
        <MonthInline />
      </Stack>
      <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Available Reports — CSV or PDF
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 1.5,
          }}
        >
          {REPORT_NAMES.map((r) => (
            <Paper key={r} variant="outlined" sx={{ p: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }}>{r}</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                <Link component="button" underline="hover" onClick={() => download(r)}>
                  CSV
                </Link>
                <Link component="button" underline="hover" onClick={() => printReport(r)}>
                  PDF
                </Link>
                {counts[r] != null ? (
                  <Typography variant="caption" color="text.secondary">
                    {counts[r]} rows
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          ))}
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Annual Budget</TableCell>
              <TableCell align="right">YTD Budget</TableCell>
              <TableCell align="right">YTD Actual</TableCell>
              <TableCell align="right">Variance</TableCell>
              <TableCell align="right">Utilization</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {catRows.map((r) => (
              <TableRow key={r.cat.letter}>
                <TableCell>{r.cat.code}</TableCell>
                <TableCell>{r.cat.name}</TableCell>
                <TableCell>{r.cat.type}</TableCell>
                <TableCell align="right">{fmt(r.annual)}</TableCell>
                <TableCell align="right">{fmt(r.ytdB)}</TableCell>
                <TableCell align="right">{fmt(r.ytdA)}</TableCell>
                <TableCell align="right" sx={{ color: toneColor(r.v >= 0 ? "pos" : "neg") }}>
                  {fmt(r.v)}
                </TableCell>
                <TableCell align="right">
                  <StatusBadge status={r.pct > 100 ? "r" : r.pct > 90 ? "a" : "g"} />
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell colSpan={2}>GRAND TOTAL</TableCell>
              <TableCell />
              <TableCell align="right">{fmt(tot.annual)}</TableCell>
              <TableCell align="right">{fmt(tot.ytdB)}</TableCell>
              <TableCell align="right">{fmt(tot.ytdA)}</TableCell>
              <TableCell
                align="right"
                sx={{ color: toneColor(tot.ytdB - tot.ytdA >= 0 ? "pos" : "neg") }}
              >
                {fmt(tot.ytdB - tot.ytdA)}
              </TableCell>
              <TableCell align="right">
                {tot.ytdB ? ((tot.ytdA / tot.ytdB) * 100).toFixed(0) : 0}%
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
